/**
 * Общая модалка админки.
 *
 * Вёрстка — Bootstrap 3 (.modal / .modal-dialog / .modal-content), как в legacy-шаблонах,
 * но поведение нормальное и одинаковое везде:
 *   • клик по затемнённому фону закрывает;
 *   • Esc закрывает;
 *   • клик внутри окна не закрывает;
 *   • пока модалка открыта, страница под ней не прокручивается;
 *   • фокус уходит в окно, при закрытии возвращается на элемент-инициатор.
 *
 * В legacy этим занимался jQuery-плагин Bootstrap; jQuery мы не подключаем, поэтому
 * поведение реализовано здесь один раз, а не копипастой на каждой странице.
 *
 * Две тонкости, из-за которых код выглядит сложнее «наивного» варианта:
 *  1. onClose держится в ref, а эффект зависит ТОЛЬКО от open. Иначе каждый ре-рендер
 *     родителя (например ввод символа в поле внутри модалки) перезапускал бы эффект,
 *     и фокус выдёргивало бы из поля обратно на контейнер диалога.
 *  2. Рендер идёт через портал в <body>. Иначе диалог остаётся внутри <form> страницы,
 *     и Enter в поле модалки отправляет ЭТУ форму (в админке — запускает рассылку).
 */

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  open: boolean
  onClose: () => void
  /** Заголовок окна. Если не задан — шапка не рисуется (как в простых legacy-модалках). */
  title?: ReactNode
  /** Кнопки внизу окна. Если не заданы — подвал не рисуется. */
  footer?: ReactNode
  children: ReactNode
  /** 'lg' — широкое окно (.modal-lg), 'sm' — узкое (.modal-sm). */
  size?: 'lg' | 'sm'
  /** Доп. классы диалога и инлайн-стили — чтобы сохранить размеры конкретных legacy-окон. */
  dialogClassName?: string
  dialogStyle?: CSSProperties
}

export function Modal({
  open,
  onClose,
  title,
  footer,
  children,
  size,
  dialogClassName,
  dialogStyle,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<Element | null>(null)

  // свежий onClose без перезапуска эффекта (см. п.1 в шапке файла)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return

    openerRef.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.classList.add('modal-open')

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)

    dialogRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      document.body.classList.remove('modal-open')
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus()
    }
  }, [open])

  if (!open) return null

  const sizeClass = size === 'lg' ? ' modal-lg' : size === 'sm' ? ' modal-sm' : ''

  return createPortal(
    <>
      <div
        className="modal fade in"
        role="dialog"
        tabIndex={-1}
        style={{ display: 'block' }}
        // закрывает клик именно по подложке, а не всплывший клик из окна
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        <div
          className={`modal-dialog${sizeClass}${dialogClassName === undefined ? '' : ' ' + dialogClassName}`}
          role="document"
          style={dialogStyle}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="modal-content">
            {title !== undefined && (
              <div className="modal-header">
                <button type="button" className="close" aria-label="Закрыть" onClick={onClose}>
                  <span aria-hidden="true">&times;</span>
                </button>
                <h4 className="modal-title">{title}</h4>
              </div>
            )}

            <div className="modal-body">{children}</div>

            {footer !== undefined && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade in" />
    </>,
    document.body,
  )
}
